import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { Panel } from '@ff-os/shared-react/components/panel/Panel';
import { UploadMethodsWindow } from '@ff-os/shared-react/components/upload-methods/UploadMethodsWindow';
import { WindowBody } from '@ff-os/shared-react/components/window/partials/WindowBody';
import { Window } from '@ff-os/shared-react/components/window/Window';
import { selectFailedUploadsCount, selectSuccessfulUploadsCount } from '@ff-scene-plugins/photo-prints-redux/slices/groupsUiSlice';
import { selectPhotoIdsByGroupId } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import classNames from 'classnames';
import { usePhotoPrintsUploadMethods } from '../../../createPhotoPrintsUploadMethods';
import { uploadImages } from '../../../thunks/uploadImages';
import { incrementWithScheduledReset } from '../../../thunks/withScheduledReset';
import { PhotoPrintsWindowErrors } from '../../PhotoPrintsWindowErrors';
import { PhotoPrintsGroupWindowNavigation } from './PhotoPrintsGroupWindowNavigation';
import { PhotoPrintsGroupWindowPanelBody } from './PhotoPrintsGroupWindowPanelBody';
import { PhotoPrintsGroupWindowPanelHeader } from './PhotoPrintsGroupWindowPanelHeader';
import styles from './PhotoPrintsGroupWindow.module.scss';

const _PhotoPrintsGroupWindow = ({
    photoIds, groupId, variantId,
    close, addPictures, onError,
    successfulUploadCount, failedUploadsCount
}: Props) => {

    const { t } = useTranslation();
    const [isBrowseOpen, setIsBrowseOpen] = useState(false);

    const panelClassName = classNames(styles.panel, {
        [styles.success]: successfulUploadCount > 0,
        [styles.error]: failedUploadsCount > 0
    });

    const uploadMethods = usePhotoPrintsUploadMethods({
        externalUpload: { groupId: groupId },
        dropzone: { onChange: addPictures, onError }
    });

    const openBrowse = useCallback(() => setIsBrowseOpen(true), [setIsBrowseOpen]);
    const closeBrowse = useCallback(() => setIsBrowseOpen(false), [setIsBrowseOpen]);

    return (
        <Window close={close} fullHeight>
            <WindowBody className={styles.windowBody}>
                <Panel className={panelClassName}>

                    <PhotoPrintsGroupWindowPanelHeader groupId={groupId} variantId={variantId} />
                    <PhotoPrintsGroupWindowPanelBody groupId={groupId}
                        browse={openBrowse}
                        close={close} />

                    <div className={styles.message}>
                        {failedUploadsCount > 0 && (
                            <>{t('photo_prints.photos_errored', { count: failedUploadsCount })} </>
                        )}

                        {successfulUploadCount > 0 && (
                            <>{t('photo_prints.photos_uploaded', { count: successfulUploadCount })}</>
                        )}
                    </div>
                </Panel>

                <UploadMethodsWindow
                    methods={uploadMethods}
                    isOpen={isBrowseOpen}
                    close={closeBrowse} />
            </WindowBody>

            <PhotoPrintsWindowErrors />
            <PhotoPrintsGroupWindowNavigation photoIds={photoIds} browse={openBrowse} close={close} />
        </Window>
    );
};

type OwnProps = {
    close: () => void;
    groupId: string;
    variantId: string;
};

type Props = OwnProps & {
    photoIds: string[];
    addPictures: (files: File[]) => void;
    onError: (errors: UploadError[]) => void;
    successfulUploadCount: number;
    failedUploadsCount: number;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    photoIds: selectPhotoIdsByGroupId(state, ownProps.groupId),
    successfulUploadCount: selectSuccessfulUploadsCount(state, ownProps.groupId),
    failedUploadsCount: selectFailedUploadsCount(state, ownProps.groupId)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    addPictures: (files: File[]) => dispatch(uploadImages(files, ownProps.groupId)),
    onError: (errors: UploadError[]) => {
        dispatch(incrementWithScheduledReset(ownProps.groupId, 'failedUploadsCount', errors.length));
    }
});

export const PhotoPrintsGroupWindow = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoPrintsGroupWindow);